import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react'
import type { IndexDef, LevelCode } from '@/api/types/readiness'
import { LEVELS, levelNum } from '@/api/types/readiness'
import { levelName } from '@/lib/indexes/registry'
import { levelToken, scoreBand } from '@/lib/readiness/score'
import { formatDate } from '@/components/common/format'

// ── Progress charts (§6.2, §9.2) ────────────────────────────────────────────
// Token-driven inline SVG/HTML so they follow the module's light and dark
// themes without a chart library. Marks are thin (2px lines, ≥8px dots with a
// surface ring, bars ≤24px with 2px surface gaps); text always wears text
// tokens; every chart has a hover layer and an aria description, and the
// Snapshots matrix is the table view of the same numbers.

const ACCENT = 'var(--rd-accent-primary)'
const SURFACE = 'var(--rd-bg-card)'

function useWidth<T extends HTMLElement>(fallback = 480): [RefObject<T>, number] {
  const ref = useRef<T>(null)
  const [w, setW] = useState(fallback)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const update = () => setW(Math.max(120, Math.floor(el.getBoundingClientRect().width)))
    update()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, w]
}

// Hover tooltip positioned inside a `position: relative` wrapper.
interface Tip { x: number; y: number; body: ReactNode }
function useTip() {
  const [tip, setTip] = useState<Tip | null>(null)
  const show = (e: { clientX: number; clientY: number; currentTarget: Element }, body: ReactNode) => {
    const host = (e.currentTarget as HTMLElement).closest('[data-chart]') as HTMLElement | null
    const r = host?.getBoundingClientRect()
    setTip({ x: e.clientX - (r?.left ?? 0), y: e.clientY - (r?.top ?? 0), body })
  }
  const hide = () => setTip(null)
  const node = tip ? (
    <div className="rd-tooltip" role="status" style={{ position: 'absolute', left: Math.max(0, tip.x + 12), top: Math.max(0, tip.y - 8), transform: 'translateY(-100%)', zIndex: 30 }}>
      {tip.body}
    </div>
  ) : null
  return { show, hide, node }
}

const shortDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })

// ── Score over time ─────────────────────────────────────────────────────────

export interface TrendPoint {
  key: string
  label: string
  date: string
  score: number
  live?: boolean
  baseline?: boolean
  unconfirmed?: number
}

export function ScoreTrend({ points, height = 168, deadline }: { points: TrendPoint[]; height?: number; deadline?: { date: string; label: string } | null }) {
  const [ref, width] = useWidth<HTMLDivElement>()
  const tip = useTip()
  const [active, setActive] = useState<number | null>(null)
  const m = { top: 14, right: 78, bottom: 26, left: 30 }
  const iw = Math.max(40, width - m.left - m.right)
  const ih = height - m.top - m.bottom
  const n = points.length
  const x = (i: number) => m.left + (n <= 1 ? iw / 2 : (iw * i) / (n - 1))
  const y = (s: number) => m.top + ih - (ih * Math.max(0, Math.min(100, s))) / 100
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.score).toFixed(1)}`).join(' ')
  const area = n > 1 ? `${path} L${x(n - 1).toFixed(1)},${y(0).toFixed(1)} L${x(0).toFixed(1)},${y(0).toFixed(1)} Z` : ''
  const slot = n > 1 ? iw / (n - 1) : iw
  const showLabel = (i: number) => i === 0 || i === n - 1 || slot >= 92
  const baselinePrefix = slot >= 130
  const bands = [25, 50, 75].map((v) => ({ v, label: scoreBand(v).label }))
  const last = points[n - 1]
  const first = points[0]
  const summary = n ? `Readiness score ${first.score} at ${first.label}${n > 1 ? ` to ${last.score} at ${last.label}` : ''}, across ${n} ${n === 1 ? 'point' : 'points'}.` : 'No readiness score yet.'

  return (
    <div ref={ref} className="relative" data-chart>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={summary} onMouseLeave={() => { tip.hide(); setActive(null) }}>
        {/* Band hairlines: recessive, labelled on the right. */}
        {bands.map((b) => (
          <g key={b.v}>
            <line x1={m.left} x2={m.left + iw} y1={y(b.v)} y2={y(b.v)} stroke="var(--rd-border-subtle)" strokeWidth={1} />
            <text x={m.left + iw + 8} y={y(b.v) + 3} fontSize={11} className="rd-axis" fill="var(--rd-text-muted)">{b.label} · {b.v}</text>
          </g>
        ))}
        <line x1={m.left} x2={m.left + iw} y1={y(0)} y2={y(0)} stroke="var(--rd-border-control)" strokeWidth={1} />
        {[0, 50, 100].map((v) => (
          <text key={v} x={m.left - 8} y={y(v) + 3} fontSize={11} className="rd-axis" textAnchor="end" fill="var(--rd-text-muted)" style={{ fontVariantNumeric: 'tabular-nums' }}>{v}</text>
        ))}
        {deadline && (
          <text x={m.left + iw} y={m.top - 4} fontSize={11} className="rd-axis" textAnchor="end" fill="var(--rd-text-muted)">{deadline.label} · {formatDate(deadline.date)}</text>
        )}
        {area && <path d={area} fill={ACCENT} opacity={0.1} />}
        {n > 1 && <path d={path} fill="none" stroke={ACCENT} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />}
        {points.map((p, i) => (
          <g key={p.key}>
            <circle cx={x(i)} cy={y(p.score)} r={active === i ? 6 : 4.5} fill={p.live ? scoreBand(p.score).token : ACCENT} stroke={SURFACE} strokeWidth={2} />
            {p.live && p.unconfirmed ? <circle cx={x(i)} cy={y(p.score)} r={9} fill="none" stroke="var(--rd-status-warn)" strokeWidth={1.5} strokeDasharray="2 3" /> : null}
            {/* Direct labels on the two points that matter: the start and the end. */}
            {(i === n - 1 || i === 0) && (
              <text x={x(i)} y={y(p.score) - 12} fontSize={11} fontWeight={600} textAnchor="middle" fill="var(--rd-text-primary)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {p.score}
              </text>
            )}
            {showLabel(i) && (
              <text x={x(i)} y={height - 8} fontSize={11} className="rd-axis" textAnchor={i === 0 && n > 1 ? 'start' : i === n - 1 && n > 1 ? 'end' : 'middle'} fill={p.live ? 'var(--rd-text-secondary)' : 'var(--rd-text-faint)'} fontWeight={p.live ? 600 : 400}>
                {p.live ? 'Live' : p.baseline && baselinePrefix ? `Baseline · ${shortDate(p.date)}` : shortDate(p.date)}
              </text>
            )}
            {/* Hit target: the whole column around the point. */}
            <rect
              x={x(i) - slot / 2}
              y={m.top}
              width={slot}
              height={ih}
              fill="transparent"
              onMouseMove={(e) => {
                setActive(i)
                const prev = i > 0 ? points[i - 1] : null
                const d = prev ? p.score - prev.score : null
                tip.show(e, (
                  <>
                    <p className="font-semibold">{p.label}{p.baseline ? ' · baseline' : ''}</p>
                    <p style={{ color: 'var(--rd-text-muted)' }}>{p.live ? 'Live · not yet saved' : formatDate(p.date)}</p>
                    <p className="mt-1"><span className="rd-mono font-semibold">{p.score}</span> · {scoreBand(p.score).label}{d != null ? <span style={{ color: d > 0 ? 'var(--rd-status-ok-text)' : d < 0 ? 'var(--rd-status-warn)' : 'var(--rd-text-muted)' }}> · {d > 0 ? '▲' : d < 0 ? '▼' : '='} {Math.abs(d)} since {prev!.label}</span> : null}</p>
                    {p.live && p.unconfirmed ? <p style={{ color: 'var(--rd-status-warn)' }}>{p.unconfirmed} suggested {p.unconfirmed === 1 ? 'level' : 'levels'} counted but not confirmed</p> : null}
                  </>
                ))
              }}
            />
          </g>
        ))}
      </svg>
      {tip.node}
    </div>
  )
}

// ── Level mix by snapshot (100% stacked bars) ───────────────────────────────

export interface LevelMixRow {
  key: string
  label: string
  sublabel?: string
  counts: Record<LevelCode, number>
  total: number
  emphasis?: boolean
}

export function LevelMixLegend({ index }: { index: IndexDef }) {
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-[11.5px]" style={{ color: 'var(--rd-text-muted)' }} aria-label="Levels">
      {LEVELS.map((l) => (
        <li key={l} className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: levelToken(l) }} aria-hidden="true" />
          <span className="rd-mono">{l}</span> {levelName(index, l)}
        </li>
      ))}
    </ul>
  )
}

export function LevelMixBars({ index, rows, barHeight = 18 }: { index: IndexDef; rows: LevelMixRow[]; barHeight?: number }) {
  const [ref, width] = useWidth<HTMLDivElement>()
  const tip = useTip()
  const labelW = 132
  const barW = Math.max(60, width - labelW - 8)
  return (
    <div ref={ref} className="relative" data-chart role="img" aria-label={rows.map((r) => `${r.label}: ${LEVELS.map((l) => `${r.counts[l]} at ${l}`).join(', ')}`).join('. ')}>
      <div className="space-y-2">
        {rows.map((r) => {
          const present = LEVELS.filter((l) => r.counts[l] > 0)
          return (
            <div key={r.key} className="flex items-center gap-2 text-[12.5px]">
              <div className="shrink-0 truncate" style={{ width: labelW }}>
                <span className="block truncate" style={{ color: r.emphasis ? 'var(--rd-text-primary)' : 'var(--rd-text-secondary)', fontWeight: r.emphasis ? 600 : 500 }}>{r.label}</span>
                {r.sublabel && <span className="rd-mono block text-[11px]" style={{ color: 'var(--rd-text-faint)' }}>{r.sublabel}</span>}
              </div>
              <div className="flex min-w-0 flex-1" style={{ height: barHeight, gap: 2 }}>
                {r.total === 0 && <div style={{ flex: 1, background: 'var(--rd-bg-tile)', borderRadius: 4 }} />}
                {present.map((l, i) => {
                  const px = (r.counts[l] / r.total) * barW
                  const first = i === 0
                  const last = i === present.length - 1
                  return (
                    <div
                      key={l}
                      style={{
                        flex: r.counts[l],
                        background: levelToken(l),
                        borderRadius: `${first ? 4 : 0}px ${last ? 4 : 0}px ${last ? 4 : 0}px ${first ? 4 : 0}px`,
                        display: 'grid',
                        placeItems: 'center',
                        color: l === 'L0' ? 'var(--rd-text-primary)' : '#fff',
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'default',
                      }}
                      onMouseMove={(e) => tip.show(e, (
                        <>
                          <p className="font-semibold">{r.label}</p>
                          <p><span className="rd-mono">{l}</span> {levelName(index, l)} · {r.counts[l]} of {r.total} objectives ({Math.round((100 * r.counts[l]) / r.total)}%)</p>
                        </>
                      ))}
                      onMouseLeave={tip.hide}
                    >
                      {px >= 22 ? r.counts[l] : ''}
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
      {tip.node}
    </div>
  )
}

// ── Domain progress: mean level now vs baseline (bullet bars) ───────────────

export interface DomainProgressRow {
  code: string
  name: string
  color: string
  now: number // mean level 0..3
  baseline: number | null
  objectives: Array<{ code: string; level: LevelCode; baseline: LevelCode | null }>
}

export function DomainProgress({ rows, baselineLabel }: { rows: DomainProgressRow[]; baselineLabel: string | null }) {
  const tip = useTip()
  const pct = (v: number) => `${(100 * Math.max(0, Math.min(3, v))) / 3}%`
  return (
    <div className="relative" data-chart>
      <ul className="space-y-2.5" aria-label="Mean level by domain, now versus baseline">
        {rows.map((r) => {
          const d = r.baseline == null ? null : Math.round((r.now - r.baseline) * 10) / 10
          return (
            <li key={r.code} className="flex items-center gap-3 text-[12.5px]">
              <span className="w-[112px] shrink-0 truncate sm:w-[132px]" style={{ color: 'var(--rd-text-secondary)' }}>{r.name}</span>
              <div
                className="relative min-w-0 flex-1"
                style={{ height: 14 }}
                onMouseMove={(e) => tip.show(e, (
                  <>
                    <p className="font-semibold">{r.name}</p>
                    <p>Mean level <span className="rd-mono">{r.now.toFixed(1)}</span> of 3{r.baseline != null ? <> · baseline <span className="rd-mono">{r.baseline.toFixed(1)}</span></> : null}</p>
                    <ul className="mt-1" style={{ color: 'var(--rd-text-muted)' }}>
                      {r.objectives.map((o) => (
                        <li key={o.code}><span className="rd-mono">{o.code}</span> {o.baseline && o.baseline !== o.level ? `${o.baseline} → ` : ''}{o.level}</li>
                      ))}
                    </ul>
                  </>
                ))}
                onMouseLeave={tip.hide}
              >
                <div className="absolute inset-y-0 left-0 right-0 my-auto" style={{ height: 10, borderRadius: 5, background: 'var(--rd-bg-tile)' }} />
                <div className="absolute inset-y-0 left-0 my-auto" style={{ height: 10, width: pct(r.now), borderRadius: 5, background: r.color, minWidth: r.now > 0 ? 6 : 0 }} />
                {r.baseline != null && (
                  <span className="absolute top-0" style={{ left: `calc(${pct(r.baseline)} - 2px)`, width: 4, height: 14, borderRadius: 2, background: 'var(--rd-text-secondary)', boxShadow: `0 0 0 2px ${SURFACE}` }} aria-hidden="true" />
                )}
                {/* Level ticks: L1, L2 hairlines. */}
                {[1, 2].map((t) => (
                  <span key={t} className="absolute inset-y-0" style={{ left: pct(t), width: 1, background: SURFACE }} aria-hidden="true" />
                ))}
              </div>
              <span className="rd-mono w-[30px] shrink-0 text-right" style={{ color: 'var(--rd-text-primary)', fontWeight: 600 }}>{r.now.toFixed(1)}</span>
              <span className="w-[44px] shrink-0 text-[11.5px]" style={{ color: d == null ? 'var(--rd-text-faint)' : d > 0 ? 'var(--rd-status-ok-text)' : d < 0 ? 'var(--rd-status-warn)' : 'var(--rd-text-faint)' }}>
                {d == null ? '' : d > 0 ? `▲ ${d.toFixed(1)}` : d < 0 ? `▼ ${Math.abs(d).toFixed(1)}` : '· same'}
              </span>
            </li>
          )
        })}
      </ul>
      <p className="mt-2 flex items-center gap-3 text-[11px]" style={{ color: 'var(--rd-text-faint)' }}>
        <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-4 rounded-sm" style={{ background: 'var(--rd-border-control)' }} /> Mean level today (0–3)</span>
        {baselineLabel && <span className="flex items-center gap-1"><span className="inline-block h-3 w-1 rounded-sm" style={{ background: 'var(--rd-text-secondary)' }} /> Baseline · {baselineLabel}</span>}
      </p>
      {tip.node}
    </div>
  )
}

// ── Per-objective level over time (step line) ───────────────────────────────

export interface LevelStep {
  key: string
  label: string
  date: string
  level: LevelCode
  live?: boolean
  unconfirmed?: boolean
}

export function LevelTrack({ index, steps, height = 96 }: { index: IndexDef; steps: LevelStep[]; height?: number }) {
  const [ref, width] = useWidth<HTMLDivElement>(300)
  const tip = useTip()
  const m = { top: 10, right: 16, bottom: 22, left: 26 }
  const iw = Math.max(40, width - m.left - m.right)
  const ih = height - m.top - m.bottom
  const n = steps.length
  const x = (i: number) => m.left + (n <= 1 ? iw / 2 : (iw * i) / (n - 1))
  const y = (l: LevelCode) => m.top + ih - (ih * levelNum(l)) / 3
  const d = steps.map((s, i) => (i === 0 ? `M${x(0)},${y(s.level)}` : `H${x(i)} V${y(s.level)}`)).join(' ')
  const slot = n > 1 ? iw / (n - 1) : iw
  return (
    <div ref={ref} className="relative" data-chart>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={steps.map((s) => `${s.label}: ${s.level}`).join(', ')} onMouseLeave={tip.hide}>
        {LEVELS.map((l) => (
          <g key={l}>
            <line x1={m.left} x2={m.left + iw} y1={y(l)} y2={y(l)} stroke="var(--rd-border-subtle)" strokeWidth={1} />
            <text x={m.left - 6} y={y(l) + 3} fontSize={11} textAnchor="end" fill="var(--rd-text-faint)" className="rd-mono">{l}</text>
          </g>
        ))}
        {n > 1 && <path d={d} fill="none" stroke="var(--rd-border-control)" strokeWidth={2} strokeLinejoin="round" />}
        {steps.map((s, i) => (
          <g key={s.key}>
            <circle cx={x(i)} cy={y(s.level)} r={4.5} fill={levelToken(s.level)} stroke={SURFACE} strokeWidth={2} />
            {s.unconfirmed && <circle cx={x(i)} cy={y(s.level)} r={8} fill="none" stroke="var(--rd-status-warn)" strokeWidth={1.5} strokeDasharray="2 3" />}
            {(i === 0 || i === n - 1 || slot >= 92) && (
              <text x={x(i)} y={height - 6} fontSize={11} className="rd-axis" textAnchor={i === 0 && n > 1 ? 'start' : i === n - 1 && n > 1 ? 'end' : 'middle'} fill={s.live ? 'var(--rd-text-secondary)' : 'var(--rd-text-faint)'} fontWeight={s.live ? 600 : 400}>
                {s.live ? 'Live' : shortDate(s.date)}
              </text>
            )}
            <rect x={x(i) - slot / 2} y={m.top} width={slot} height={ih} fill="transparent" onMouseMove={(e) => tip.show(e, (
              <>
                <p className="font-semibold">{s.label}</p>
                <p style={{ color: 'var(--rd-text-muted)' }}>{s.live ? 'Live' : formatDate(s.date)}</p>
                <p><span className="rd-mono">{s.level}</span> {levelName(index, s.level)}{s.unconfirmed ? ' · suggested, not confirmed' : ''}</p>
              </>
            ))} />
          </g>
        ))}
      </svg>
      {tip.node}
    </div>
  )
}

// Shared card chrome for a chart block.
export function ChartBlock({ title, hint, right, children, style }: { title: ReactNode; hint?: ReactNode; right?: ReactNode; children: ReactNode; style?: CSSProperties }) {
  return (
    <div style={style}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="rd-label">{title}</p>
          {hint && <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-faint)' }}>{hint}</p>}
        </div>
        {right}
      </div>
      <div className="mt-3">{children}</div>
    </div>
  )
}
